import React, { useMemo, useState } from 'react';
import { Banknote, Receipt, Search } from 'lucide-react';
import { useUIColors } from '../../../hooks/useTheme';
import {
  buildPoPaymentScheduleRows,
  getPoPaymentStatusPresentation,
  isPoAwaitingPaymentStatus,
  resolvePoLifecycleStatuses,
} from '../../placeholders/purchaseOrderSummary';

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BUCKET_FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'vencido', label: 'Vencidos' },
  { key: 'hoy', label: 'Hoy' },
  { key: 'por_vencer', label: '7 días' },
  { key: 'futuro', label: 'Futuros' },
  { key: 'pagado', label: 'Pagados' },
];

const bucketPresentation = (bucket, isDarkMode) => {
  switch (bucket) {
    case 'vencido':
      return {
        label: 'Vencido',
        color: '#dc2626',
        bg: isDarkMode ? 'rgba(220,38,38,0.22)' : 'rgba(220,38,38,0.12)',
      };
    case 'hoy':
      return {
        label: 'Vence hoy',
        color: '#ea580c',
        bg: isDarkMode ? 'rgba(234,88,12,0.22)' : 'rgba(234,88,12,0.12)',
      };
    case 'por_vencer':
      return {
        label: 'Por vencer',
        color: '#d97706',
        bg: isDarkMode ? 'rgba(217,119,6,0.22)' : 'rgba(217,119,6,0.12)',
      };
    case 'pagado':
      return {
        label: 'Pagado',
        color: '#15803d',
        bg: isDarkMode ? 'rgba(21,128,61,0.22)' : 'rgba(21,128,61,0.12)',
      };
    case 'futuro':
      return {
        label: 'Programado',
        color: '#2563eb',
        bg: isDarkMode ? 'rgba(37,99,235,0.22)' : 'rgba(37,99,235,0.12)',
      };
    default:
      return {
        label: 'Sin fecha',
        color: isDarkMode ? '#a1a1aa' : '#64748b',
        bg: isDarkMode ? 'rgba(161,161,170,0.15)' : 'rgba(100,116,139,0.1)',
      };
  }
};

const daysLabel = (daysUntilDue, bucket) => {
  if (bucket === 'pagado') return '—';
  if (daysUntilDue == null) return '—';
  if (daysUntilDue === 0) return 'Hoy';
  if (daysUntilDue < 0) return `${Math.abs(daysUntilDue)} d atrasado`;
  return `En ${daysUntilDue} d`;
};

/**
 * Calendario de pagos de OC: vencimiento = emisión factura (o fecha OC) + condición de pago.
 */
const FinancePurchasePaymentsTable = ({
  orders = [],
  loading = false,
  error = '',
  isDarkMode = false,
  onRowClick,
  onMarkPaid,
  onInvoice,
}) => {
  const ui = useUIColors();
  const [searchQuery, setSearchQuery] = useState('');
  const [bucketFilter, setBucketFilter] = useState('all');

  const scheduleRows = useMemo(() => buildPoPaymentScheduleRows(orders), [orders]);

  const bucketCounts = useMemo(() => {
    const counts = { all: scheduleRows.length };
    scheduleRows.forEach((row) => {
      counts[row.bucket] = (counts[row.bucket] || 0) + 1;
    });
    return counts;
  }, [scheduleRows]);

  const filteredRows = useMemo(() => {
    let list = scheduleRows;
    if (bucketFilter !== 'all') {
      list = list.filter((row) => row.bucket === bucketFilter);
    }
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.po_code,
          row.supplier_name,
          row.supplier_rut,
          row.payment_terms,
          row.invoiceFolios,
          row.status,
          row.dueIso,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    return list;
  }, [scheduleRows, bucketFilter, searchQuery]);

  const totals = useMemo(() => {
    const open = filteredRows.filter((row) => row.bucket !== 'pagado');
    const amount = open.reduce((acc, row) => acc + Number(row.payment_amount || 0), 0);
    const overdue = filteredRows
      .filter((row) => row.bucket === 'vencido')
      .reduce((acc, row) => acc + Number(row.payment_amount || 0), 0);
    return { count: open.length, amount, overdue };
  }, [filteredRows]);

  const padV = 9;
  const padH = 10;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const iconButtonStyle = (enabled) => ({
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${inputBorder}`,
    background: enabled ? inputBg : isDarkMode ? '#27272a' : '#f5f5f4',
    color: enabled ? ui.text : ui.muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.45,
    padding: 0,
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {BUCKET_FILTERS.map((item) => {
            const active = bucketFilter === item.key;
            const count = bucketCounts[item.key] || 0;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setBucketFilter(item.key)}
                style={{
                  appearance: 'none',
                  border: `1px solid ${active ? ui.interactive || '#16a34a' : inputBorder}`,
                  background: active
                    ? isDarkMode
                      ? 'rgba(22,163,74,0.18)'
                      : 'rgba(22,163,74,0.1)'
                    : inputBg,
                  color: active ? ui.text : ui.muted,
                  borderRadius: 8,
                  padding: '5px 10px',
                  fontSize: 12,
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {item.label}
                <span style={{ fontSize: 11, opacity: 0.85 }}>{count}</span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: ui.muted }}>
            {totals.count} por pagar · {formatMoney(totals.amount)}
            {totals.overdue > 0 ? ` · vencido ${formatMoney(totals.overdue)}` : ''}
          </span>
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 34,
              padding: '0 10px',
              borderRadius: 8,
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              minWidth: 220,
            }}
          >
            <Search size={14} color={ui.muted} />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar OC, proveedor, folio…"
              style={{
                border: 'none',
                outline: 'none',
                background: 'transparent',
                color: ui.text,
                fontSize: 13,
                width: '100%',
              }}
            />
          </label>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
            color: ui.text,
          }}
        >
          <thead>
            <tr style={{ background: ui.header }}>
              {[
                'Vencimiento',
                'Plazo',
                'OC',
                'Proveedor',
                'Cond. pago',
                'Fecha base',
                'Folio',
                'Monto',
                'Pago',
                '',
              ].map((label) => (
                <th
                  key={label || 'actions'}
                  style={{
                    textAlign: label === 'Monto' || label === '' ? 'right' : 'left',
                    padding: `${padV}px ${padH}px`,
                    fontSize: 11,
                    fontWeight: 700,
                    color: ui.muted,
                    borderBottom: `1px solid ${ui.border}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, color: ui.muted, textAlign: 'center' }}>
                  Cargando pagos…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, color: '#dc2626', textAlign: 'center' }}>
                  {error}
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: 24, color: ui.muted, textAlign: 'center' }}>
                  No hay pagos para los filtros seleccionados.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => {
                const due = bucketPresentation(row.bucket, isDarkMode);
                const lifecycle = resolvePoLifecycleStatuses(row);
                const payment = getPoPaymentStatusPresentation(lifecycle.payment_status, isDarkMode);
                const canPay =
                  lifecycle.payment_status === 'Por Pagar' ||
                  lifecycle.payment_status === 'Vencido' ||
                  lifecycle.payment_status === 'Pago Parcial' ||
                  isPoAwaitingPaymentStatus(row.status);
                return (
                  <tr
                    key={row.id}
                    onClick={() => onRowClick?.(row)}
                    style={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      borderBottom: `1px solid ${ui.border}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = ui.rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <td style={{ padding: `${padV}px ${padH}px`, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 700 }}>{formatDate(row.dueIso)}</div>
                      <span
                        style={{
                          display: 'inline-block',
                          marginTop: 3,
                          fontSize: 11,
                          fontWeight: 600,
                          color: due.color,
                          background: due.bg,
                          borderRadius: 999,
                          padding: '1px 7px',
                        }}
                      >
                        {due.label}
                        {row.bucket !== 'pagado' && row.daysUntilDue != null
                          ? ` · ${daysLabel(row.daysUntilDue, row.bucket)}`
                          : ''}
                      </span>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, color: ui.muted, whiteSpace: 'nowrap' }}>
                      {row.paymentDays === 0
                        ? row.paymentKind === 'cash'
                          ? 'Contado'
                          : '0 días'
                        : `${row.paymentDays} días`}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, fontWeight: 700 }}>{row.po_code || '—'}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, minWidth: 140 }}>
                      <div style={{ fontWeight: 600 }}>{row.supplier_name || '—'}</div>
                      {row.supplier_rut ? (
                        <div style={{ fontSize: 11, color: ui.muted, marginTop: 1 }}>{row.supplier_rut}</div>
                      ) : null}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px` }}>{row.payment_terms || '—'}</td>
                    <td style={{ padding: `${padV}px ${padH}px`, whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{formatDate(row.iso)}</div>
                      <div style={{ fontSize: 11, color: ui.muted, marginTop: 1 }}>
                        {row.source === 'invoice' ? 'Emisión factura' : row.source === 'order' ? 'Fecha OC' : '—'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, color: ui.muted }}>
                      {row.invoiceFolios || '—'}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        textAlign: 'right',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatMoney(row.payment_amount, row.currency)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 11,
                          fontWeight: 600,
                          color: payment.color,
                          background: payment.bg,
                          border: `1px solid ${payment.border}`,
                          borderRadius: 999,
                          padding: '2px 8px',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {payment.label}
                      </span>
                    </td>
                    <td
                      style={{ padding: `${padV}px ${padH}px`, textAlign: 'right' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span style={{ display: 'inline-flex', gap: 4, justifyContent: 'flex-end' }}>
                        {onMarkPaid ? (
                          <button
                            type="button"
                            style={iconButtonStyle(canPay)}
                            disabled={!canPay}
                            title={canPay ? 'Marcar como pagada' : 'Disponible solo en Por Pagar'}
                            aria-label="Marcar como pagada"
                            onClick={() => canPay && onMarkPaid(row)}
                          >
                            <Banknote size={14} />
                          </button>
                        ) : null}
                        {onInvoice ? (
                          <button
                            type="button"
                            style={iconButtonStyle(true)}
                            title="Facturación"
                            aria-label="Facturación"
                            onClick={() => onInvoice(row)}
                          >
                            <Receipt size={14} />
                          </button>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancePurchasePaymentsTable;
